import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/Sheet';
import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system/theme/reviewDesk';
import { useTranslation } from '../../hooks/useTranslation';
import {
  frameworkDisplayName,
  toggleFrameworkId,
  type LinkableFramework,
} from './documentFrameworkLinking';
import { checkboxA11y } from '@/utils/checkboxA11y';

interface DocumentFrameworkLinkSheetProps {
  visible: boolean;
  fileName: string;
  frameworks: LinkableFramework[];
  /** Upload with the selected framework ids (may be empty). */
  onConfirm: (frameworkIds: number[]) => void;
  /** Close without uploading. */
  onCancel: () => void;
}

/**
 * Lets a school admin link a document to frameworks before it is uploaded
 * (de-slop ADM-9: replaces the `window.prompt` that asked for numeric IDs).
 */
export default function DocumentFrameworkLinkSheet({
  visible,
  fileName,
  frameworks,
  onConfirm,
  onCancel,
}: DocumentFrameworkLinkSheetProps) {
  const { t, language } = useTranslation();
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    if (visible) setSelected([]);
  }, [visible]);

  return (
    <Sheet
      visible={visible}
      onClose={onCancel}
      title={t('schoolManagement.documentLinkSheet.title')}
      closeLabel={t('common.close')}
      testID="document-link-sheet"
    >
      <Text style={styles.description}>{t('schoolManagement.documentLinkSheet.description')}</Text>
      <Text style={styles.fileName} numberOfLines={1} testID="document-link-file">
        {t('schoolManagement.documentLinkSheet.file', { filename: fileName })}
      </Text>

      <View style={styles.list}>
        {frameworks.map((framework) => {
          const checked = selected.includes(framework.id);
          return (
            <Pressable
              key={framework.id}
              testID={`document-link-framework-${framework.id}`}
              onPress={() => setSelected((prev) => toggleFrameworkId(prev, framework.id))}
              {...checkboxA11y(checked)}
              style={[styles.row, checked && styles.rowChecked]}
            >
              <MaterialIcons
                name={checked ? 'check-box' : 'check-box-outline-blank'}
                size={22}
                color={checked ? reviewDesk.accent : reviewDesk.inkSoft}
              />
              <Text style={styles.rowText} numberOfLines={2}>
                {frameworkDisplayName(framework, language)}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <View style={styles.actions}>
        <Button
          variant="outline"
          onPress={() => onConfirm([])}
          testID="document-link-skip"
          className="flex-1"
        >
          <Text>{t('schoolManagement.documentLinkSheet.uploadWithoutLinking')}</Text>
        </Button>
        <Button
          variant="default"
          onPress={() => onConfirm(selected)}
          disabled={selected.length === 0}
          testID="document-link-confirm"
          className="flex-1"
        >
          <Text>
            {t('schoolManagement.documentLinkSheet.uploadLinked', { count: selected.length })}
          </Text>
        </Button>
      </View>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  description: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    lineHeight: 20,
  },
  fileName: {
    marginTop: 8,
    color: reviewDesk.muted,
    fontSize: 13,
  },
  list: {
    marginTop: 14,
    gap: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minHeight: 48,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
  },
  rowChecked: {
    borderColor: reviewDesk.accent,
    backgroundColor: reviewDesk.accentSoft,
  },
  rowText: {
    flex: 1,
    color: reviewDesk.ink,
    fontSize: 15,
  },
  actions: {
    marginTop: 18,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
});
